import { useState, useEffect } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Plus, Download, RefreshCw, Shield, ShieldAlert, RotateCw, XCircle, Search } from 'lucide-react'
import {
  listCertificates,
  enrollCertificate,
  renewCertificate,
  revokeCertificate,
  collectCertificate,
  type CertEnrollRequest,
  type CertRenewRequest,
  type CertRevokeRequest,
} from '@/lib/api'
import { queryClient } from '@/lib/query-client'
import { useMe } from '@/lib/use-me'
import { AppShell } from '@/components/layout/app-shell'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/spinner'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toast } from 'sonner'
import { formatDate } from '@/lib/utils'

export default function CertificateManagement() {
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false)
  const [renewDialogOpen, setRenewDialogOpen] = useState(false)
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false)
  const [selectedCert, setSelectedCert] = useState<{ cert_name: string; cert_type?: number } | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const { isAdmin, isLoading: meLoading } = useMe()

  // Debounce search input (500ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput)
    }, 500)
    return () => clearTimeout(timer)
  }, [searchInput])

  // Determine if search is likely a cert_id (numeric) or cert_name
  const searchParams = debouncedSearch.trim()
    ? /^\d+$/.test(debouncedSearch.trim())
      ? { certId: debouncedSearch.trim(), certName: undefined }
      : { certName: debouncedSearch.trim(), certId: undefined }
    : { certName: undefined, certId: undefined }

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['certificates', searchParams.certName, searchParams.certId],
    queryFn: () => listCertificates(searchParams.certName, searchParams.certId),
    // Certificates are admin-only; don't even fire the request for customers.
    enabled: isAdmin,
  })

  // Customers have no certificate access at all — show nothing but a notice.
  // (The backend also enforces this with a 403, and the nav link is hidden.)
  if (!meLoading && !isAdmin) {
    return (
      <AppShell title="Certificates">
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <ShieldAlert className="h-10 w-10 text-[var(--fg-muted)]" />
            <CardTitle>Access restricted</CardTitle>
            <CardDescription>
              Certificate management is available to administrators only.
            </CardDescription>
          </CardContent>
        </Card>
      </AppShell>
    )
  }

  const enrollMutation = useMutation({
    mutationFn: enrollCertificate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certificates'] })
      setEnrollDialogOpen(false)
      toast.success('Certificate enrolled successfully')
    },
    onError: (error) => {
      toast.error(`Failed to enroll certificate: ${error.message}`)
    },
  })

  const collectMutation = useMutation({
    mutationFn: ({ certName }: { certName: string }) => collectCertificate(certName),
    onSuccess: (data) => {
      // Download the full chain (server cert + CA bundle)
      const blob = new Blob([data.full_chain || ''], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${data.cert_name}-fullchain.pem`
      a.click()
      toast.success('Certificate bundle downloaded')
    },
    onError: (error) => {
      toast.error(`Failed to download certificate: ${error.message}`)
    },
  })

  const renewMutation = useMutation({
    mutationFn: renewCertificate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certificates'] })
      setRenewDialogOpen(false)
      setSelectedCert(null)
      toast.success('Certificate renewed successfully')
    },
    onError: (error) => {
      toast.error(`Failed to renew certificate: ${error.message}`)
    },
  })

  const revokeMutation = useMutation({
    mutationFn: revokeCertificate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certificates'] })
      setRevokeDialogOpen(false)
      setSelectedCert(null)
      toast.success('Certificate revoked successfully')
    },
    onError: (error) => {
      toast.error(`Failed to revoke certificate: ${error.message}`)
    },
  })

  const handleEnroll = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const subjAltNames = (formData.get('subj_alt_names') as string)?.trim()
    const request: CertEnrollRequest = {
      cert_type: parseInt(formData.get('cert_type') as string),
      csr: formData.get('csr') as string,
      comments: formData.get('comments') as string,
      ownergroup: formData.get('ownergroup') as string,
      requester_emails: formData.get('requester_emails') as string,
      term: parseInt(formData.get('term') as string) || 365,
      ...(subjAltNames && { subj_alt_names: subjAltNames }),
    }
    enrollMutation.mutate(request)
  }

  const handleRenew = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!selectedCert) return
    const formData = new FormData(e.currentTarget)
    const subjAltNames = (formData.get('subj_alt_names') as string)?.trim()
    const request: CertRenewRequest = {
      cert_name: selectedCert.cert_name,
      cert_type: parseInt(formData.get('cert_type') as string),
      csr: formData.get('csr') as string,
      comments: formData.get('comments') as string,
      ownergroup: formData.get('ownergroup') as string,
      requester_emails: formData.get('requester_emails') as string,
      term: parseInt(formData.get('term') as string) || 30,
      ...(subjAltNames && { subj_alt_names: subjAltNames }),
    }
    renewMutation.mutate(request)
  }

  const handleRevoke = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!selectedCert) return
    const formData = new FormData(e.currentTarget)
    const request: CertRevokeRequest = {
      cert_name: selectedCert.cert_name,
      reason: formData.get('reason') as string,
    }
    revokeMutation.mutate(request)
  }

  return (
    <AppShell title="Certificates">
      <div className="space-y-6">
        <div className="flex justify-between">
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
          <Button onClick={() => setEnrollDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Enroll Certificate
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>SSL/TLS Certificates ({data?.total || 0})</CardTitle>
            <CardDescription>Manage public certificates for your services</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Search box - always visible */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--fg-muted)]" />
                <Input
                  placeholder="Search by certificate name or ID..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-10"
                />
                {searchInput && (
                  <p className="text-xs text-[var(--fg-muted)] mt-1">
                    Searching {/^\d+$/.test(searchInput.trim()) ? 'by ID' : 'by name'}...
                  </p>
                )}
              </div>

              {/* Results */}
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <Spinner />
                </div>
              ) : data && data.certificates.length > 0 ? (
                <div className="space-y-2">
                  {data.certificates.map((cert) => (
                  <div
                    key={cert.cert_id}
                    className="flex items-center justify-between p-4 rounded-lg border border-[var(--border)] hover:border-[var(--primary)] transition-colors"
                  >
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-[var(--primary)]" />
                        <span className="font-medium text-[var(--fg)]">{cert.cert_name}</span>
                        <Badge variant={cert.status === 'active' ? 'success' : 'warning'}>
                          {cert.status || 'Active'}
                        </Badge>
                      </div>
                      <div className="flex gap-4 text-sm text-[var(--fg-muted)]">
                        {cert.issue_date && (
                          <span>Issued: {formatDate(cert.issue_date)}</span>
                        )}
                        {cert.expiry_date && (
                          <span className="font-medium">Expires: {formatDate(cert.expiry_date)}</span>
                        )}
                      </div>
                      {cert.ownergroup && (
                        <p className="text-xs text-[var(--fg-muted)]">Owner: {cert.ownergroup}</p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => collectMutation.mutate({ certName: cert.cert_name })}
                        title="Download certificate"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedCert({ cert_name: cert.cert_name, cert_type: cert.cert_type })
                          setRenewDialogOpen(true)
                        }}
                        title="Renew certificate"
                      >
                        <RotateCw className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedCert({ cert_name: cert.cert_name })
                          setRevokeDialogOpen(true)
                        }}
                        title="Revoke certificate"
                      >
                        <XCircle className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
                </div>
              ) : (
                <div className="text-center py-8 text-[var(--fg-muted)]">
                  {searchInput ? 'No certificates found matching your search.' : 'No certificates found. Enroll your first certificate to get started.'}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Dialog open={enrollDialogOpen} onOpenChange={setEnrollDialogOpen}>
          <DialogContent className="max-w-2xl">
            <form onSubmit={handleEnroll}>
              <DialogHeader>
                <DialogTitle>Enroll Certificate</DialogTitle>
                <DialogDescription>Request a new SSL/TLS certificate</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 my-4">
                <div>
                  <label className="text-sm font-medium">Certificate Type</label>
                  <select
                    name="cert_type"
                    className="w-full h-10 rounded-md border border-[var(--border)] bg-[var(--bg)] px-3 text-sm"
                    required
                  >
                    <option value="31480">31480 - Entrust DV TLS</option>
                    <option value="31481">31481 - Entrust OV TLS</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">CSR (PEM format)</label>
                  <textarea
                    name="csr"
                    className="w-full min-h-[120px] rounded-md border border-[var(--border)] bg-[var(--bg)] px-3 py-2 text-sm font-mono"
                    placeholder="-----BEGIN CERTIFICATE REQUEST-----&#10;...&#10;-----END CERTIFICATE REQUEST-----"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Owner Group</label>
                    <Input name="ownergroup" placeholder="aip-platform" required />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Term (days)</label>
                    <Input name="term" type="number" defaultValue="365" required />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Requester Emails</label>
                  <Input name="requester_emails" placeholder="team@teradata.com" required />
                </div>
                <div>
                  <label className="text-sm font-medium">Comments</label>
                  <Input name="comments" placeholder="Certificate for AIP platform" required />
                </div>
                <div>
                  <label className="text-sm font-medium">Subject Alternative Names (SANs)</label>
                  <Input
                    name="subj_alt_names"
                    placeholder="example.com,*.example.com,app.example.com"
                  />
                  <p className="text-xs text-[var(--fg-muted)] mt-1">
                    Optional: Comma-separated list of additional domain names for the certificate
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEnrollDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={enrollMutation.isPending}>
                  {enrollMutation.isPending ? <Spinner size="sm" /> : 'Enroll'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={renewDialogOpen} onOpenChange={setRenewDialogOpen}>
          <DialogContent className="max-w-2xl">
            <form onSubmit={handleRenew}>
              <DialogHeader>
                <DialogTitle>Renew Certificate</DialogTitle>
                <DialogDescription>
                  Renew {selectedCert?.cert_name} with a new CSR
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 my-4">
                <div>
                  <label className="text-sm font-medium">Certificate Type</label>
                  <select
                    name="cert_type"
                    className="w-full h-10 rounded-md border border-[var(--border)] bg-[var(--bg)] px-3 text-sm"
                    defaultValue={selectedCert?.cert_type || 31480}
                    required
                  >
                    <option value="31480">31480 - Entrust DV TLS</option>
                    <option value="31481">31481 - Entrust OV TLS</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">New CSR (PEM format)</label>
                  <textarea
                    name="csr"
                    className="w-full min-h-[120px] rounded-md border border-[var(--border)] bg-[var(--bg)] px-3 py-2 text-sm font-mono"
                    placeholder="-----BEGIN CERTIFICATE REQUEST-----&#10;...&#10;-----END CERTIFICATE REQUEST-----"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Owner Group</label>
                    <Input name="ownergroup" placeholder="aip-platform" required />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Term (days)</label>
                    <Input name="term" type="number" defaultValue="30" required />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Requester Emails</label>
                  <Input name="requester_emails" placeholder="team@teradata.com" required />
                </div>
                <div>
                  <label className="text-sm font-medium">Comments</label>
                  <Input name="comments" placeholder="Renewing certificate" required />
                </div>
                <div>
                  <label className="text-sm font-medium">Subject Alternative Names (SANs)</label>
                  <Input
                    name="subj_alt_names"
                    placeholder="example.com,*.example.com,app.example.com"
                  />
                  <p className="text-xs text-[var(--fg-muted)] mt-1">
                    Optional: Comma-separated list of additional domain names
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setRenewDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={renewMutation.isPending}>
                  {renewMutation.isPending ? <Spinner size="sm" /> : 'Renew'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
          <DialogContent>
            <form onSubmit={handleRevoke}>
              <DialogHeader>
                <DialogTitle>Revoke Certificate</DialogTitle>
                <DialogDescription>
                  Revoke {selectedCert?.cert_name}. This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 my-4">
                <div>
                  <label className="text-sm font-medium">Reason for Revocation</label>
                  <select
                    name="reason"
                    className="w-full h-10 rounded-md border border-[var(--border)] bg-[var(--bg)] px-3 text-sm"
                    required
                  >
                    <option value="">Select a reason...</option>
                    <option value="unspecified">Unspecified</option>
                    <option value="keyCompromise">Key Compromise</option>
                    <option value="affiliationChanged">Affiliation Changed</option>
                    <option value="superseded">Superseded</option>
                    <option value="cessationOfOperation">Cessation of Operation</option>
                  </select>
                  <p className="text-xs text-[var(--fg-muted)] mt-1">
                    Select the reason for revoking this certificate
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setRevokeDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={revokeMutation.isPending} variant="destructive">
                  {revokeMutation.isPending ? <Spinner size="sm" /> : 'Revoke'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  )
}
